import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { projectsAPI, tasksAPI, usersAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, ArrowLeft, DollarSign, ClipboardCheck, Star, Link2, Copy } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { DeliveryReviewForm, HealthScoreCard, HealthHistoryChart } from '../components/ClientEvaluation';
import axios from 'axios';

const API = '/api';
const api = axios.create({ baseURL: API });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const ProjectDetail = () => {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [showRevenueDialog, setShowRevenueDialog] = useState(false);
  const [revenueFormData, setRevenueFormData] = useState({ revenue_eur_monthly: '', notes: '' });
  const [health, setHealth] = useState(null);
  const [healthHistory, setHealthHistory] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [surveyLink, setSurveyLink] = useState(null);
  const [taskFormData, setTaskFormData] = useState({
    title: '',
    description: '',
    status: 'todo',
    priority: 'medium',
    assigned_to: '',
    estimated_hours: '',
    due_date: ''
  });

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    loadData();
  }, [id]);

  useEffect(() => {
    if (id) loadHealthData();
  }, [selectedMonth]);

  const loadData = async () => {
    try {
      const [projectRes, tasksRes, usersRes] = await Promise.all([
        projectsAPI.getById(id),
        tasksAPI.getAll(id),
        usersAPI.getAll()
      ]);
      setProject(projectRes.data);
      setTasks(tasksRes.data);
      setUsers(usersRes.data);
      
      // Load health data
      loadHealthData();
    } catch (error) {
      toast.error('Failed to load project details');
      navigate('/projects');
    }
  };

  const loadHealthData = async () => {
    try {
      const [healthRes, historyRes] = await Promise.all([
        api.get(`/projects/${id}/health?month=${selectedMonth}`),
        api.get(`/projects/${id}/health-history?months=12`)
      ]);
      setHealth(healthRes.data);
      setHealthHistory(historyRes.data.history || []);
    } catch (err) {
      console.error('Error loading health data:', err);
    }
  };

  const createSurveyLink = async () => {
    try {
      const period = selectedMonth;
      const res = await api.post(`/projects/${id}/survey?period=${period}`);
      const fullUrl = `${window.location.origin}/survey/${res.data.token}`;
      setSurveyLink(fullUrl);
      toast.success('Link generat cu succes!');
    } catch (err) {
      toast.error('Eroare la generarea link-ului');
    }
  };

  const copyToClipboard = async (text) => {
    await navigator.clipboard.writeText(text);
    toast.success('Link copiat în clipboard!');
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...taskFormData,
        project_id: id,
        estimated_hours: taskFormData.estimated_hours ? parseFloat(taskFormData.estimated_hours) : null,
        due_date: taskFormData.due_date ? new Date(taskFormData.due_date).toISOString() : null,
        assigned_to: taskFormData.assigned_to || null
      };
      
      if (editingTask) {
        await tasksAPI.update(editingTask.id, payload);
        toast.success('Taskul a fost actualizat!');
      } else {
        await tasksAPI.create(payload);
        toast.success('Taskul a fost creat!');
      }
      
      setShowTaskDialog(false);
      setEditingTask(null);
      setTaskFormData({ title: '', description: '', status: 'todo', priority: 'medium', assigned_to: '', estimated_hours: '', due_date: '' });
      loadData();
    } catch (error) {
      toast.error(editingTask ? 'Eroare la actualizarea taskului' : 'Eroare la crearea taskului');
    }
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setTaskFormData({
      title: task.title || '',
      description: task.description || '',
      status: task.status || 'todo',
      priority: task.priority || 'medium',
      assigned_to: task.assigned_to || '',
      estimated_hours: task.estimated_hours || '',
      due_date: task.due_date ? task.due_date.split('T')[0] : ''
    });
    setShowTaskDialog(true);
  };

  const handleCloseTaskDialog = () => {
    setShowTaskDialog(false);
    setEditingTask(null);
    setTaskFormData({ title: '', description: '', status: 'todo', priority: 'medium', assigned_to: '', estimated_hours: '', due_date: '' });
  };

  const handleUpdateTaskStatus = async (taskId, newStatus) => {
    try {
      const task = tasks.find(t => t.id === taskId);
      await tasksAPI.update(taskId, { ...task, status: newStatus });
      toast.success('Status actualizat');
      loadData();
    } catch (error) {
      toast.error('Eroare la actualizarea statusului');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Ești sigur că vrei să ștergi acest task?')) return;
    try {
      await tasksAPI.delete(taskId);
      toast.success('Taskul a fost șters');
      loadData();
    } catch (error) {
      toast.error('Eroare la ștergerea taskului');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      todo: 'bg-gray-100 text-gray-700',
      in_progress: 'bg-blue-100 text-blue-700',
      done: 'bg-green-100 text-green-700',
      blocked: 'bg-red-100 text-red-700'
    };
    return colors[status] || colors.todo;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-gray-100 text-gray-700',
      medium: 'bg-yellow-100 text-yellow-700',
      high: 'bg-orange-100 text-orange-700',
      urgent: 'bg-red-100 text-red-700'
    };
    return colors[priority] || colors.medium;
  };

  const handleUpdateRevenue = async (e) => {
    e.preventDefault();
    try {
      const API_URL = '/api';
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/projects/${id}/revenue`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          revenue_eur_monthly: parseFloat(revenueFormData.revenue_eur_monthly),
          notes: revenueFormData.notes
        })
      });
      toast.success('Revenue updated successfully');
      setShowRevenueDialog(false);
      setRevenueFormData({ revenue_eur_monthly: '', notes: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to update revenue');
    }
  };

  if (!project) return null;

  const tasksByStatus = {
    todo: tasks.filter(t => t.status === 'todo'),
    in_progress: tasks.filter(t => t.status === 'in_progress'),
    done: tasks.filter(t => t.status === 'done'),
    blocked: tasks.filter(t => t.status === 'blocked')
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="icon" onClick={() => navigate('/projects')} data-testid="back-btn">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="font-outfit text-4xl font-bold tracking-tight" data-testid="project-detail-title">{project.name}</h1>
              <p className="mt-2 text-muted-foreground">{project.description || 'Fără descriere'}</p>
              {isAdmin && project.revenue_eur_monthly && (
                <p className="mt-1 text-sm font-medium text-brand-orange">
                  Revenue: €{project.revenue_eur_monthly.toLocaleString()}/lună
                </p>
              )}
            </div>
          </div>
          <div className="flex gap-3">
            {isAdmin && (
              <Dialog open={showRevenueDialog} onOpenChange={setShowRevenueDialog}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="edit-revenue-btn">
                    <DollarSign className="mr-2 h-4 w-4" />
                    {project.revenue_eur_monthly ? 'Editează Revenue' : 'Setează Revenue'}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="font-outfit text-2xl">Actualizare Revenue Proiect</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleUpdateRevenue} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="revenue">Revenue EUR/lună *</Label>
                      <Input
                        id="revenue"
                        type="number"
                        step="0.01"
                        placeholder={project.revenue_eur_monthly?.toString() || "Ex: 5000.00"}
                        value={revenueFormData.revenue_eur_monthly}
                        onChange={(e) => setRevenueFormData({ ...revenueFormData, revenue_eur_monthly: e.target.value })}
                        required
                        data-testid="revenue-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        Curent: {project.revenue_eur_monthly ? `€${project.revenue_eur_monthly.toLocaleString()}` : 'Nesetat'}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="notes">Note (opțional)</Label>
                      <Textarea
                        id="notes"
                        placeholder="Ex: Contract actualizat pentru Q1 2026"
                        value={revenueFormData.notes}
                        onChange={(e) => setRevenueFormData({ ...revenueFormData, notes: e.target.value })}
                        rows={2}
                        data-testid="revenue-notes-input"
                      />
                    </div>
                    <div className="flex justify-end gap-3">
                      <Button type="button" variant="outline" onClick={() => setShowRevenueDialog(false)}>
                        Anulează
                      </Button>
                      <Button type="submit" className="bg-brand-orange hover:bg-brand-orange/90" data-testid="submit-revenue-btn">
                        Actualizează Revenue
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
            <Dialog open={showTaskDialog} onOpenChange={handleCloseTaskDialog}>
              <DialogTrigger asChild>
                <Button className="bg-brand-orange hover:bg-brand-orange/90" data-testid="create-task-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  Task Nou
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-outfit text-2xl">
                  {editingTask ? 'Editează Task' : 'Creează Task Nou'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateTask} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Titlu Task *</Label>
                  <Input
                    id="title"
                    value={taskFormData.title}
                    onChange={(e) => setTaskFormData({ ...taskFormData, title: e.target.value })}
                    placeholder="ex: Implementare landing page"
                    required
                    data-testid="task-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Descriere</Label>
                  <Textarea
                    id="description"
                    value={taskFormData.description}
                    onChange={(e) => setTaskFormData({ ...taskFormData, description: e.target.value })}
                    rows={3}
                    placeholder="Detalii despre task..."
                    data-testid="task-description-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    <Select value={taskFormData.status} onValueChange={(value) => setTaskFormData({ ...taskFormData, status: value })}>
                      <SelectTrigger data-testid="task-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todo">De făcut</SelectItem>
                        <SelectItem value="in_progress">În progres</SelectItem>
                        <SelectItem value="done">Finalizat</SelectItem>
                        <SelectItem value="blocked">Blocat</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="priority">Prioritate</Label>
                    <Select value={taskFormData.priority} onValueChange={(value) => setTaskFormData({ ...taskFormData, priority: value })}>
                      <SelectTrigger data-testid="task-priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Scăzută</SelectItem>
                        <SelectItem value="medium">Medie</SelectItem>
                        <SelectItem value="high">Ridicată</SelectItem>
                        <SelectItem value="urgent">Urgentă</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="assigned_to">Atribuie la</Label>
                    <Select value={taskFormData.assigned_to} onValueChange={(value) => setTaskFormData({ ...taskFormData, assigned_to: value })}>
                      <SelectTrigger data-testid="task-assign-select">
                        <SelectValue placeholder="Selectează persoana" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map(user => (
                          <SelectItem key={user.id} value={user.id}>{user.full_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="estimated_hours">Timp Estimat (max 60 min)</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="estimated_hours"
                        type="number"
                        min="0"
                        max="60"
                        value={taskFormData.estimated_hours ? Math.round(parseFloat(taskFormData.estimated_hours) * 60) : ''}
                        onChange={(e) => {
                          const mins = Math.min(parseInt(e.target.value) || 0, 60);
                          setTaskFormData({ ...taskFormData, estimated_hours: (mins / 60).toString() });
                        }}
                        placeholder="ex: 30"
                        className="w-24"
                        data-testid="task-minutes-input"
                      />
                      <span className="text-sm text-muted-foreground">minute</span>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="due_date">Data Limită</Label>
                  <Input
                    id="due_date"
                    type="date"
                    value={taskFormData.due_date}
                    onChange={(e) => setTaskFormData({ ...taskFormData, due_date: e.target.value })}
                    data-testid="task-due-date-input"
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={handleCloseTaskDialog}>
                    Anulează
                  </Button>
                  <Button type="submit" className="bg-brand-orange hover:bg-brand-orange/90" data-testid="submit-task-btn">
                    {editingTask ? 'Salvează Modificările' : 'Creează Task'}
                  </Button>
                </div>
              </form>
            </DialogContent>
            </Dialog>
          </div>
        </div>

        <Tabs defaultValue="kanban">
          <TabsList>
            <TabsTrigger value="kanban" data-testid="kanban-tab">Kanban Board</TabsTrigger>
            <TabsTrigger value="list" data-testid="list-tab">Task List</TabsTrigger>
            <TabsTrigger value="evaluation" data-testid="evaluation-tab" className="flex items-center gap-1">
              <ClipboardCheck className="w-4 h-4" />
              Evaluare
            </TabsTrigger>
          </TabsList>

          <TabsContent value="kanban" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {Object.entries(tasksByStatus).map(([status, statusTasks]) => (
                <Card key={status} data-testid={`column-${status}`}>
                  <CardHeader className="border-b border-border">
                    <CardTitle className="text-sm font-medium uppercase tracking-wider">
                      {status.replace('_', ' ')}
                      <span className="ml-2 text-muted-foreground">({statusTasks.length})</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="space-y-3">
                      {statusTasks.map((task) => {
                        const assignedUser = users.find(u => u.id === task.assigned_to);
                        return (
                          <div key={task.id} className="border border-border rounded-md p-3 hover:shadow-md transition-smooth" data-testid="task-card">
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="font-medium text-sm">{task.title}</h4>
                              <div className="flex gap-1">
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-6 w-6"
                                  onClick={() => handleEditTask(task)}
                                  data-testid="edit-task-btn"
                                >
                                  <Edit2 className="h-3 w-3" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-6 w-6 text-red-500 hover:text-red-700"
                                  onClick={() => handleDeleteTask(task.id)}
                                  data-testid="delete-task-btn"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                            {task.description && (
                              <p className="text-xs text-muted-foreground mb-2">{task.description}</p>
                            )}
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium uppercase tracking-wide ${getPriorityColor(task.priority)}`}>
                                {task.priority}
                              </span>
                            </div>
                            {assignedUser && (
                              <p className="text-xs text-muted-foreground mb-2">Assigned: {assignedUser.full_name}</p>
                            )}
                            {task.due_date && (
                              <p className="text-xs text-muted-foreground mb-2">Due: {new Date(task.due_date).toLocaleDateString()}</p>
                            )}
                            <Select value={task.status} onValueChange={(value) => handleUpdateTaskStatus(task.id, value)}>
                              <SelectTrigger className="h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="todo">To Do</SelectItem>
                                <SelectItem value="in_progress">In Progress</SelectItem>
                                <SelectItem value="done">Done</SelectItem>
                                <SelectItem value="blocked">Blocked</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="list" className="mt-6">
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/20 border-b border-border">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Task</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Priority</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Assigned</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Due Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tasks.map((task) => {
                        const assignedUser = users.find(u => u.id === task.assigned_to);
                        return (
                          <tr key={task.id} className="border-b border-border hover:bg-muted/50" data-testid="task-row">
                            <td className="px-4 py-3">
                              <div>
                                <p className="font-medium text-sm">{task.title}</p>
                                {task.description && (
                                  <p className="text-xs text-muted-foreground">{task.description}</p>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium uppercase tracking-wide ${getStatusColor(task.status)}`}>
                                {task.status.replace('_', ' ')}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium uppercase tracking-wide ${getPriorityColor(task.priority)}`}>
                                {task.priority}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm">{assignedUser ? assignedUser.full_name : '-'}</td>
                            <td className="px-4 py-3 text-sm">{task.due_date ? new Date(task.due_date).toLocaleDateString() : '-'}</td>
                            <td className="px-4 py-3">
                              <div className="flex gap-1">
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8"
                                  onClick={() => handleEditTask(task)}
                                  data-testid="edit-task-action-btn"
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8 text-red-500 hover:text-red-700"
                                  onClick={() => handleDeleteTask(task.id)}
                                  data-testid="delete-task-action-btn"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {tasks.length === 0 && (
                    <div className="py-12 text-center text-muted-foreground">
                      No tasks yet. Create your first task to get started.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="evaluation" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column - Health Score */}
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Star className="w-5 h-5 text-amber-500" />
                          Health Score
                        </CardTitle>
                        <CardDescription>Scorul de sănătate al proiectului</CardDescription>
                      </div>
                      <Input
                        type="month"
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        className="w-40"
                      />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <HealthScoreCard health={health} />
                  </CardContent>
                </Card>

                {/* Health History Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Istoric Scoruri</CardTitle>
                    <CardDescription>Evoluția în ultimele 12 luni</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <HealthHistoryChart history={healthHistory} />
                  </CardContent>
                </Card>

                {/* Client Survey Section */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Link2 className="w-5 h-5 text-blue-500" />
                      Survey Client
                    </CardTitle>
                    <CardDescription>
                      Generează un link pentru chestionarul de satisfacție client
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <Button onClick={createSurveyLink} variant="outline" data-testid="generate-survey-btn">
                      Generează Link Survey
                    </Button>
                    
                    {surveyLink && (
                      <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                        <Input value={surveyLink} readOnly className="flex-1" data-testid="survey-link-input" />
                        <Button
                          size="icon"
                          variant="outline"
                          onClick={() => copyToClipboard(surveyLink)}
                          data-testid="copy-survey-link-btn"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    
                    <p className="text-xs text-muted-foreground">
                      Trimite acest link clientului pentru a completa chestionarul de satisfacție.
                      Frecvență recomandată: trimestrial.
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Right Column - Delivery Review */}
              <div>
                <DeliveryReviewForm 
                  projectId={id} 
                  month={selectedMonth}
                  onSaved={loadHealthData}
                />
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};